import { useContext } from 'react'
import { DateTime } from 'luxon'

import {
  Box, Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { useForm, useSetState } from '@domain-group/react-hooks'

import useCampaignQuoteTemplate, { generateUrls } from '@hooks/useCampaignQuoteTemplate'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import { AgencyModel } from '@models/types'

const MODAL_KEY = 'DownloadQuoteTemplateModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const defaultState = {
  disclaimerAccepted: false,
  loading: false,
}

const DownloadQuoteTemplateModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const campaignQuoteTemplate = modalPayload?.campaignQuoteTemplate ?? {}

  const [state, setState] = useSetState(defaultState)
  const { disclaimerAccepted, loading } = state

  const { agency } : { agency: Partial<AgencyModel>} = useCampaignQuoteTemplate(campaignQuoteTemplate)
  const agencyQuoteTemplateDisplayData = agency?.quote_template_display_data || {}

  const defaultCampaignQuoteTemplateState = {
    agent_display_name: agencyQuoteTemplateDisplayData?.agent_display_name || 'John Doe',
    campaign_display_name: agencyQuoteTemplateDisplayData?.campaign_display_name
    || `${agency?.address_line_1}, ${agency?.address_region} ${agency?.address_postcode} ${agency?.address_state}`,
    title: agencyQuoteTemplateDisplayData?.quote_template_display_name || campaignQuoteTemplate.title || '',
    vendor_display_name: agencyQuoteTemplateDisplayData?.vendor_display_name || 'Test Vendor',
  }

  const campaignQuoteTemplateForm = useForm(
    defaultCampaignQuoteTemplateState,
    {
      entity: {
        ...campaignQuoteTemplate,
        title: campaignQuoteTemplate.title,
      },
      requiredFields: [],
      validateOn: 'change',
    },
    [campaignQuoteTemplate.id, campaignQuoteTemplate.cache_key],
  )

  const {
    entityState,
    entityState: {
      agent_display_name,
      campaign_display_name,
      title,
      vendor_display_name,
    },
    handlers,
  } = campaignQuoteTemplateForm

  const { pdfDownloadUrl } = generateUrls(
    { id: campaignQuoteTemplate.id },
    { payload: entityState as Record<string, string> },
  )

  const dateAndTime = DateTime.now().toFormat("EEE d'th' LLL yyyy, h:mm a")

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Download"
        titleSecondLine="Quote Template"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Campaign Address/Title">
            <input
              name="campaign_display_name"
              type="text"
              value={campaign_display_name}
              placeholder="eg. 123 Test Street, Suburb"
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Vendor Display Name" marginTop="large">
            <input name="vendor_display_name" type="text" value={vendor_display_name} {...handlers} />
          </Form.Field>

          <Form.Field label="Agent Display Name" marginTop="large">
            <input name="agent_display_name" type="text" value={agent_display_name} {...handlers} />
          </Form.Field>

          <Form.Field label="Quote Template Display Name" marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <SidebarNoticeBox boxProps={{ marginTop: 'large', marginBottom: 'none' }}>
            <SidebarNoticeBox.Title>Disclaimer</SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              {`You are downloading a static PDF copy of this quote template as of ${dateAndTime}.
              Please be aware that prices and items on this template can become outdated.`}
            </SidebarNoticeBox.Paragraph>

            <SidebarNoticeBox.Paragraph>
              {`This file will not update automatically when you make changes in the platform.
              To ensure you are always providing accurate, up-to-date pricing to prospective vendors,
              we highly recommend generating a fresh proposal directly from Realhub/Engage for every new client.`}
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>

          <SidebarModal.SelectItem
            callbacks={{ selectItem: () => setState({ disclaimerAccepted: !disclaimerAccepted }) }}
            selected={disclaimerAccepted}
            title="I understand this is a static file and may become outdated."
          />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Box>
          <AuthenticatedDownload
            callbacks={{
              afterAction: () => {
                setState({ loading: false })
                return closeModal()
              },
              beforeAction: () => setState({ loading: true }),
            }}
            toastMessage="Downloading PDF, please wait..."
            key="download"
          >
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="primaryCreate"
              href={pdfDownloadUrl}
              loading={loading}
              loadingBubbleColor="white"
              disabled={!disclaimerAccepted}
              size="large"
            >
              Download Quote Template
            </Button>
          </AuthenticatedDownload>
        </Box>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DownloadQuoteTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
